/**
 * Light / dark theme for VIVA HUB + scheduler staff chrome.
 * Persists to localStorage and sets html[data-hub-theme] on staff routes.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'

export const HUB_THEME_STORAGE_KEY = 'viva-hub-theme'

const HubThemeContext = createContext(null)

function isStaffPath(pathname) {
  return pathname.startsWith('/hub') || pathname.startsWith('/scheduler')
}

function readStoredTheme() {
  try {
    const stored = localStorage.getItem(HUB_THEME_STORAGE_KEY)
    if (stored === 'dark' || stored === 'light') return stored
  } catch {
    /* ignore */
  }
  return 'light'
}

export function applyHubThemeToDocument(theme) {
  const root = document.documentElement
  root.dataset.hubTheme = theme
  root.style.colorScheme = theme
}

export function clearHubThemeFromDocument() {
  const root = document.documentElement
  delete root.dataset.hubTheme
  root.style.colorScheme = ''
}

export function HubThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof document !== 'undefined' && document.documentElement.dataset.hubTheme === 'dark') {
      return 'dark'
    }
    return readStoredTheme()
  })

  useEffect(() => {
    try {
      localStorage.setItem(HUB_THEME_STORAGE_KEY, theme)
    } catch {
      /* ignore */
    }
  }, [theme])

  const setTheme = useCallback((next) => {
    if (next === 'dark' || next === 'light') {
      setThemeState(next)
    }
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }, [])

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === 'dark',
      setTheme,
      toggleTheme,
    }),
    [theme, setTheme, toggleTheme]
  )

  return <HubThemeContext.Provider value={value}>{children}</HubThemeContext.Provider>
}

/** Keeps html[data-hub-theme] in sync on staff routes only. Must render inside Router. */
export function HubThemeDocumentSync() {
  const { theme } = useHubTheme()
  const { pathname } = useLocation()

  useEffect(() => {
    if (isStaffPath(pathname)) {
      applyHubThemeToDocument(theme)
    } else {
      clearHubThemeFromDocument()
    }
  }, [pathname, theme])

  return null
}

export function useHubTheme() {
  const ctx = useContext(HubThemeContext)
  if (!ctx) {
    throw new Error('useHubTheme must be used within HubThemeProvider')
  }
  return ctx
}
